import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { useAuth } from '../contexts/AuthContext';

const S = {
  page: { minHeight: '100vh', background: 'var(--bg-primary, #0f0f1a)', color: 'var(--text-primary, #e8e8f0)' },
  content: { maxWidth: 1280, margin: '0 auto', padding: '24px 16px 60px' },
  twoCol: { display: 'flex', gap: 24, alignItems: 'flex-start' },
  leftCol: { width: 480, flexShrink: 0, position: 'sticky', top: 16 },
  rightCol: { flex: 1, minWidth: 0 },
  backBtn: { background: 'none', border: 'none', color: 'var(--text-secondary, #8888aa)', cursor: 'pointer', fontSize: 13, padding: '8px 0', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 },
  card: { background: 'var(--bg-card, #1a1a2e)', border: '1px solid var(--border-color, #2a2a3e)', borderRadius: 12, padding: 24, marginBottom: 20 },
  title: { fontSize: 17, fontWeight: 700, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #8888aa)', marginBottom: 6, marginTop: 16 },
  input: { width: '100%', padding: '10px 14px', background: 'var(--bg-input, #12121f)', border: '1px solid var(--border-color, #2a2a3e)', borderRadius: 8, color: 'var(--text-primary, #e8e8f0)', fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  textarea: { width: '100%', padding: '10px 14px', background: 'var(--bg-input, #12121f)', border: '1px solid var(--border-color, #2a2a3e)', borderRadius: 8, color: 'var(--text-primary, #e8e8f0)', fontSize: 14, boxSizing: 'border-box', resize: 'vertical', minHeight: 100, outline: 'none', fontFamily: 'inherit' },
  sendBtn: { marginTop: 20, width: '100%', padding: 13, background: 'linear-gradient(135deg, #6c63ff, #5a52d5)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  sendBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  statsRow: { display: 'flex', gap: 12 },
  statBox: { flex: 1, background: 'var(--bg-input, #12121f)', borderRadius: 8, padding: '12px 16px', textAlign: 'center' },
  statNum: { fontSize: 24, fontWeight: 800, color: '#6c63ff' },
  statLabel: { fontSize: 11, color: 'var(--text-secondary, #8888aa)', marginTop: 4 },
  resultBox: { marginTop: 16, padding: '14px 16px', borderRadius: 8, fontSize: 14, lineHeight: 1.6 },
  resultSuccess: { background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#4ade80' },
  resultError: { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171' },
  historyItem: { padding: '16px 0', borderBottom: '1px solid var(--border-color, #2a2a3e)' },
  historyTitle: { fontWeight: 600, marginBottom: 4, fontSize: 14 },
  historyBody: { color: 'var(--text-secondary, #8888aa)', fontSize: 13, marginBottom: 8, whiteSpace: 'pre-wrap' },
  historyMeta: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeSuccess: { background: 'rgba(34,197,94,0.15)', color: '#4ade80' },
  badgeFail: { background: 'rgba(239,68,68,0.15)', color: '#f87171' },
  badgeRead: { background: 'rgba(108,99,255,0.2)', color: '#a09af8', cursor: 'pointer' },
  badgeUnread: { background: 'rgba(251,191,36,0.15)', color: '#fbbf24' },
  badgeIndividual: { background: 'rgba(56,189,248,0.15)', color: '#38bdf8' },
  progressBar: { height: 4, borderRadius: 4, background: '#2a2a3e', marginTop: 8, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, background: 'linear-gradient(90deg,#6c63ff,#4ade80)', transition: 'width .4s' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modal: { background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: 14, padding: 28, maxWidth: 520, width: '100%', maxHeight: '85vh', overflow: 'auto' },
  modalTitle: { fontSize: 17, fontWeight: 700, marginBottom: 4 },
  modalBody: { fontSize: 14, color: '#aaa', lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 16 },
  tabRow: { display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #2a2a3e' },
  tab: { flex: 1, padding: '9px 0', textAlign: 'center', fontSize: 13, fontWeight: 600, cursor: 'pointer', borderBottom: '2px solid transparent', color: '#8888aa' },
  tabActive: { borderBottom: '2px solid #6c63ff', color: '#6c63ff' },
  personRow: { display: 'flex', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #2a2a3e', gap: 12 },
  avatar: { width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  closeBtn: { width: '100%', marginTop: 16, padding: '10px', background: '#2a2a3e', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14 },
  // 발송 대상 선택 탭
  modeRow: { display: 'flex', gap: 0, marginBottom: 20, background: '#12121f', borderRadius: 10, padding: 4 },
  modeBtn: { flex: 1, padding: '9px 0', textAlign: 'center', fontSize: 14, fontWeight: 600, cursor: 'pointer', borderRadius: 8, border: 'none', background: 'transparent', color: '#8888aa', transition: 'all 0.15s' },
  modeBtnActive: { background: 'linear-gradient(135deg,#6c63ff,#5a52d5)', color: '#fff' },
  // 사원 검색
  empRow: { display: 'flex', gap: 10, alignItems: 'stretch', flexWrap: 'wrap' },
  empSearchCol: { position: 'relative', flex: '1 1 220px', minWidth: 0 },
  empSelect: {
    flex: '0 0 auto', minWidth: 200, maxWidth: '100%', padding: '10px 12px',
    background: 'var(--bg-input, #12121f)', border: '1px solid var(--border-color, #2a2a3e)',
    borderRadius: 8, color: 'var(--text-primary, #e8e8f0)', fontSize: 14, cursor: 'pointer', boxSizing: 'border-box',
  },
  empSearch: { position: 'relative' },
  empDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 2000, background: '#1a1a2e', border: '1px solid #2a2a3e', borderRadius: 8, maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' },
  empItem: { padding: '10px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #2a2a3e' },
  selectedEmp: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(108,99,255,0.15)', border: '1px solid rgba(108,99,255,0.4)', borderRadius: 8, marginTop: 8 },
};

function avatarColor(name) {
  const colors = ['#6c63ff', '#4ade80', '#f472b6', '#fb923c', '#38bdf8'];
  let h = 0;
  for (const c of (name || '?')) h = (h * 31 + c.charCodeAt(0)) % colors.length;
  return colors[h];
}

function fmt(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('ko-KR'); } catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getMonth()+1}/${d.getDate()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  } catch { return iso; }
}
function getTokenFromUser(user) {
  if (user && typeof user === 'object' && user.token) return String(user.token);
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    return stored?.token ? String(stored.token) : '';
  } catch { return ''; }
}
function authHdr(user) {
  const t = getTokenFromUser(user);
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

/** 이름·이메일·@앞 아이디까지 안전하게 부분 검색 (대소문자 무시) */
function empMatchesQuery(e, rawQuery) {
  const q = String(rawQuery || '').trim().toLowerCase();
  if (!q) return true;
  const name = String(e?.name ?? '').trim().toLowerCase();
  const email = String(e?.email ?? '').trim().toLowerCase();
  const local = email.includes('@') ? email.split('@')[0] : email;
  return name.includes(q) || email.includes(q) || local.includes(q);
}

function sortEmployees(list) {
  return [...list].sort((a, b) => {
    const na = String(a?.name || '').localeCompare(String(b?.name || ''), 'ko');
    if (na !== 0) return na;
    return String(a?.email || '').localeCompare(String(b?.email || ''), 'ko');
  });
}

// ── 사원 선택 컴포넌트 (검색 + 드롭다운) ─────────────────────────────────────────
function EmployeeSelector({ employees, selected, onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const sorted = React.useMemo(() => sortEmployees(employees || []), [employees]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = React.useMemo(() => {
    const list = sorted.filter(e => empMatchesQuery(e, query));
    return list.slice(0, 80);
  }, [sorted, query]);

  const handleSelectEmail = (email) => {
    if (!email) {
      onSelect(null);
      return;
    }
    const found = sorted.find(x => String(x.email || '') === email);
    if (found) onSelect(found);
  };

  return (
    <div style={S.empRow}>
      <div ref={ref} style={S.empSearchCol}>
        {selected ? (
          <div style={S.selectedEmp}>
            <div style={{ ...S.avatar, width: 32, height: 32, fontSize: 13, background: avatarColor(selected.name), flexShrink: 0 }}>
              {(selected.name || '?').charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{selected.name || '(이름 없음)'}</div>
              <div style={{ color: '#8888aa', fontSize: 12 }}>{selected.email}</div>
            </div>
            {!selected.hasToken && (
              <span style={{ fontSize: 11, color: '#f87171', background: 'rgba(239,68,68,0.15)', padding: '2px 7px', borderRadius: 10 }}>토큰없음</span>
            )}
            <button type="button" onClick={() => { onSelect(null); setQuery(''); }}
              style={{ background: 'none', border: 'none', color: '#8888aa', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
          </div>
        ) : (
          <>
            <input
              type="text"
              autoComplete="off"
              style={S.input}
              placeholder="이름·이메일·아이디 입력 (한글 검색)"
              value={query}
              onChange={e => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setOpen(false);
              }}
            />
            {open && (
              <div style={S.empDropdown}>
                {sorted.length === 0 ? (
                  <div style={{ padding: '16px', color: '#8888aa', fontSize: 13, textAlign: 'center' }}>사원 목록이 없습니다</div>
                ) : filtered.length === 0 ? (
                  <div style={{ padding: '16px', color: '#8888aa', fontSize: 13, textAlign: 'center' }}>검색 결과 없음</div>
                ) : (
                  filtered.map(e => (
                    <div key={e.email} role="option" style={{ ...S.empItem, background: 'transparent' }}
                      onMouseEnter={ev => { ev.currentTarget.style.background = '#2a2a3e'; }}
                      onMouseLeave={ev => { ev.currentTarget.style.background = 'transparent'; }}
                      onClick={() => { onSelect(e); setQuery(''); setOpen(false); }}>
                      <div style={{ ...S.avatar, width: 30, height: 30, fontSize: 12, background: avatarColor(e.name), flexShrink: 0 }}>
                        {(e.name || e.email || '?').charAt(0)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 600 }}>{e.name || '(이름 없음)'}</span>
                        <span style={{ color: '#8888aa', marginLeft: 8, fontSize: 12 }}>{e.email}</span>
                      </div>
                      {e.hasToken
                        ? <span style={{ fontSize: 11, color: '#4ade80', flexShrink: 0 }}>📱</span>
                        : <span style={{ fontSize: 11, color: '#f87171', flexShrink: 0 }}>—</span>}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {!selected && (
        <select
          style={S.empSelect}
          value=""
          aria-label="수신자 목록에서 선택"
          onChange={(e) => handleSelectEmail(e.target.value)}
        >
          <option value="">▼ 목록에서 선택</option>
          {sorted.map(e => (
            <option key={e.email} value={e.email}>
              {(e.name || e.email || '').trim()} — {e.email}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// ── 메인 페이지 ────────────────────────────────────────────────────────────────
export default function NotificationsPage() {
  const navigate = useNavigate();
  const apiUrl = getApiBaseUrl();
  const { user } = useAuth();

  // 발송 폼
  const [mode, setMode] = useState('all'); // 'all' | 'individual'
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // 데이터
  const [tokenCount, setTokenCount] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);

  // 이력 필터
  const PAGE_SIZE = 30;
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [filterType, setFilterType] = useState('all');      // 'all' | 'broadcast' | 'individual'
  const [filterDateFrom, setFilterDateFrom] = useState(monthAgo);
  const [filterDateTo, setFilterDateTo] = useState(today);
  const [filterKeyword, setFilterKeyword] = useState('');
  const [historyPage, setHistoryPage] = useState(0);
  // 실제 적용된 필터 (조회 버튼 클릭 시 반영)
  const [appliedFilter, setAppliedFilter] = useState({
    type: 'all', dateFrom: monthAgo, dateTo: today, keyword: '', page: 0,
  });

  // 읽음 현황 모달
  const [readersModal, setReadersModal] = useState(null);
  const [readersLoading, setReadersLoading] = useState(false);
  const [readersTab, setReadersTab] = useState('read');

  // 삭제 확인 모달
  const [deleteTarget, setDeleteTarget] = useState(null); // { seq, title }
  const [deleting, setDeleting] = useState(false);

  const fetchTokenCount = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/erp/push/token-count`, { headers: authHdr(user) });
      if (res.ok) { const j = await res.json(); setTokenCount(j.count ?? 0); }
    } catch { setTokenCount(null); }
  }, [apiUrl, user]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/erp/push/employees`, { headers: authHdr(user) });
      if (res.ok) { const j = await res.json(); setEmployees(j.employees || []); }
    } catch { /* ignore */ }
  }, [apiUrl, user]);

  const fetchHistory = useCallback(async (filter) => {
    setHistoryLoading(true);
    try {
      const f = filter || appliedFilter;
      const q = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((f.page || 0) * PAGE_SIZE),
      });
      if (f.dateFrom) q.set('date_from', f.dateFrom);
      if (f.dateTo)   q.set('date_to',   f.dateTo);
      if (f.type && f.type !== 'all') q.set('push_type', f.type);
      if (f.keyword && f.keyword.trim()) q.set('keyword', f.keyword.trim());

      const res = await fetch(`${apiUrl}/api/erp/push/history?${q}`, { headers: authHdr(user) });
      if (res.ok) {
        const j = await res.json();
        setHistory(j.items || []);
        setHistoryTotal(j.total ?? (j.items || []).length);
      }
    } catch { /* ignore */ } finally { setHistoryLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, user]);

  // 필터 적용 조회
  const handleSearch = useCallback(() => {
    const newFilter = {
      type: filterType, dateFrom: filterDateFrom,
      dateTo: filterDateTo, keyword: filterKeyword, page: 0,
    };
    setAppliedFilter(newFilter);
    setHistoryPage(0);
    fetchHistory(newFilter);
  }, [filterType, filterDateFrom, filterDateTo, filterKeyword, fetchHistory]);

  // 페이지 이동
  const handlePageChange = useCallback((newPage) => {
    const newFilter = { ...appliedFilter, page: newPage };
    setAppliedFilter(newFilter);
    setHistoryPage(newPage);
    fetchHistory(newFilter);
  }, [appliedFilter, fetchHistory]);

  useEffect(() => {
    if (!user) return;
    fetchTokenCount();
    fetchEmployees();
    fetchHistory(appliedFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTokenCount, fetchEmployees]);

  const handleSend = useCallback(async () => {
    if (!title.trim() || !body.trim()) return;
    if (mode === 'individual' && !selectedEmp) return;
    setSending(true); setResult(null);
    try {
      let endpoint, payload;
      if (mode === 'all') {
        endpoint = `${apiUrl}/api/erp/push/send-all`;
        payload = { title: title.trim(), body: body.trim() };
      } else {
        endpoint = `${apiUrl}/api/erp/push/send-one`;
        payload = { title: title.trim(), body: body.trim(), target_email: selectedEmp.email };
      }
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: authHdr(user),
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.detail || `HTTP ${res.status}`);
      setResult({ ok: json.success !== false, data: json, mode, target: selectedEmp });
      setTitle(''); setBody('');
      if (mode === 'individual') setSelectedEmp(null);
      setTimeout(() => fetchHistory(appliedFilter), 800);
    } catch (e) {
      setResult({ ok: false, msg: String(e?.message || e) });
    } finally { setSending(false); }
  }, [apiUrl, user, mode, title, body, selectedEmp, fetchHistory, appliedFilter]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // 1) erp_tweets soft-delete + erp_push_read 삭제
      const r1 = await fetch(`${apiUrl}/api/erp/push/history/${deleteTarget.seq}`, {
        method: 'DELETE', headers: authHdr(user),
      });
      // 2) push_list에서 ext_seq 연결된 개인 알림 전체 삭제
      await fetch(`${apiUrl}/api/push/admin/by-ext/${deleteTarget.seq}`, {
        method: 'DELETE', headers: authHdr(user),
      }).catch(() => {});
      if (!r1.ok) {
        const j = await r1.json().catch(() => ({}));
        alert(`삭제 실패: ${j.detail || r1.status}`);
        return;
      }
      setHistory(prev => prev.filter(h => h.seq !== deleteTarget.seq));
      setDeleteTarget(null);
    } catch (e) {
      alert(`삭제 중 오류: ${e.message}`);
    } finally {
      setDeleting(false);
    }
  }, [apiUrl, user, deleteTarget]);

  const openReaders = useCallback(async (pushSeq) => {
    setReadersModal({ push_seq: pushSeq, data: null });
    setReadersTab('read');
    setReadersLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/erp/push/history/${pushSeq}/readers`, { headers: authHdr(user) });
      if (res.ok) { const data = await res.json(); setReadersModal({ push_seq: pushSeq, data }); }
    } catch { /* ignore */ } finally { setReadersLoading(false); }
  }, [apiUrl, user]);

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending
    && (mode === 'all' || (mode === 'individual' && selectedEmp));

  const tokenCountInMode = mode === 'all'
    ? tokenCount
    : (selectedEmp ? (selectedEmp.hasToken ? '1명' : '⚠️ 토큰없음') : null);

  return (
    <div style={S.page}>
      <Header />
      <div style={S.content}>
        <button style={S.backBtn} onClick={() => navigate('/')}>← 대시보드로</button>

        {/* ── 2열 레이아웃 ── */}
        <div style={S.twoCol}>

        {/* 왼쪽: 현황 + 발송 폼 */}
        <div style={S.leftCol}>

        {/* 현황 */}
        <div style={S.card}>
          <div style={S.title}>📊 발송 대상 현황</div>
          <div style={S.statsRow}>
            <div style={S.statBox}>
              <div style={S.statNum}>{tokenCount === null ? '…' : tokenCount}</div>
              <div style={S.statLabel}>등록된 기기 수</div>
            </div>
            <div style={S.statBox}>
              <div style={S.statNum}>{employees.filter(e => e.hasToken).length}</div>
              <div style={S.statLabel}>FCM 등록 직원</div>
            </div>
            <div style={S.statBox}>
              <div style={S.statNum}>{history.length}</div>
              <div style={S.statLabel}>발송 이력</div>
            </div>
          </div>
          <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--text-secondary, #8888aa)', lineHeight: 1.5 }}>
            수신자 목록·전체 발송·등록 기기 수는 <strong style={{ color: '#a09af8' }}>엠알티(MRT) 그룹</strong> 소속만 포함됩니다.
          </p>
        </div>

        {/* 발송 폼 */}
        <div style={S.card}>
          <div style={S.title}>📢 공지 푸시 발송</div>

          {/* 발송 대상 선택 */}
          <div style={S.modeRow}>
            <button
              style={{ ...S.modeBtn, ...(mode === 'all' ? S.modeBtnActive : {}) }}
              onClick={() => { setMode('all'); setSelectedEmp(null); setResult(null); }}
            >
              📣 전체 발송
            </button>
            <button
              style={{ ...S.modeBtn, ...(mode === 'individual' ? S.modeBtnActive : {}) }}
              onClick={() => { setMode('individual'); setResult(null); }}
            >
              👤 개별 발송
            </button>
          </div>

          {/* 개별 발송: 사원 선택 */}
          {mode === 'individual' && (
            <>
              <label style={{ ...S.label, marginTop: 0 }}>수신자 선택</label>
              <EmployeeSelector
                employees={employees}
                selected={selectedEmp}
                onSelect={setSelectedEmp}
              />
              {selectedEmp && !selectedEmp.hasToken && (
                <div style={{ marginTop: 8, color: '#fbbf24', fontSize: 13 }}>
                  ⚠️ 이 직원은 앱에 FCM 토큰이 등록되어 있지 않습니다. 발송해도 수신되지 않을 수 있습니다.
                </div>
              )}
            </>
          )}

          <label style={S.label}>제목</label>
          <input style={S.input} placeholder="공지사항 제목" value={title}
            onChange={e => setTitle(e.target.value)} maxLength={100} />

          <label style={S.label}>메시지</label>
          <textarea style={S.textarea} placeholder="전달할 내용을 입력하세요"
            value={body} onChange={e => setBody(e.target.value)} maxLength={500} />

          <button
            style={{ ...S.sendBtn, ...(canSend ? {} : S.sendBtnDisabled) }}
            disabled={!canSend}
            onClick={handleSend}
          >
            {sending
              ? '발송 중…'
              : mode === 'all'
                ? `📲 전체 발송${tokenCount !== null ? ` (${tokenCount}명)` : ''}`
                : selectedEmp
                  ? `📲 ${selectedEmp.name}님에게 발송`
                  : '📲 개별 발송'}
          </button>

          {result && (
            <div style={{ ...S.resultBox, ...(result.ok ? S.resultSuccess : S.resultError) }}>
              {result.ok
                ? result.mode === 'individual'
                  ? <>✅ <strong>{result.target?.name}</strong>님에게 발송 완료 ({result.data.sent}개 기기 수신)</>
                  : <>✅ 발송 완료 — 총 {result.data.total}명 중 <strong>{result.data.sent}명</strong> 성공{result.data.failed > 0 && `, ${result.data.failed}명 실패`}</>
                : <>❌ 발송 실패: {result.msg}</>}
            </div>
          )}
        </div>

        </div>{/* /leftCol */}

        {/* 오른쪽: 발송 이력 */}
        <div style={S.rightCol}>
        <div style={S.card}>
          <div style={{ ...S.title, justifyContent: 'space-between', marginBottom: 14 }}>
            <span>📋 발송 이력</span>
            <button onClick={() => fetchHistory(appliedFilter)}
              style={{ background: 'none', border: 'none', color: '#6c63ff', cursor: 'pointer', fontSize: 13 }}>
              {historyLoading ? '로딩 중…' : '↻ 새로고침'}
            </button>
          </div>

          {/* ── 필터 바 ── */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end',
            padding: '14px 16px', background: '#12121f', borderRadius: 10, marginBottom: 16 }}>

            {/* 타입 선택 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#555566', fontWeight: 700 }}>발송 구분</span>
              <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden',
                border: '1px solid #2a2a3e' }}>
                {[['all','전체'],['broadcast','전체공지'],['individual','개별']].map(([k, label]) => (
                  <button key={k} onClick={() => setFilterType(k)}
                    style={{ padding: '6px 12px', border: 'none', fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s',
                      background: filterType === k ? 'linear-gradient(135deg,#6c63ff,#5a52d5)' : '#1a1a2e',
                      color: filterType === k ? '#fff' : '#8888aa',
                      borderRight: k !== 'individual' ? '1px solid #2a2a3e' : 'none' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 시작일 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#555566', fontWeight: 700 }}>시작일</span>
              <input type="date" value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                style={{ ...S.input, width: 140, padding: '7px 10px', fontSize: 13 }} />
            </div>

            {/* 종료일 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, color: '#555566', fontWeight: 700 }}>종료일</span>
              <input type="date" value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                style={{ ...S.input, width: 140, padding: '7px 10px', fontSize: 13 }} />
            </div>

            {/* 키워드 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 160px', minWidth: 120 }}>
              <span style={{ fontSize: 11, color: '#555566', fontWeight: 700 }}>제목 검색</span>
              <input type="text" placeholder="제목 키워드…" value={filterKeyword}
                onChange={e => setFilterKeyword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                style={{ ...S.input, padding: '7px 10px', fontSize: 13 }} />
            </div>

            {/* 조회 버튼 */}
            <button onClick={handleSearch} disabled={historyLoading}
              style={{ padding: '7px 20px', background: 'linear-gradient(135deg,#6c63ff,#5a52d5)',
                border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700,
                fontSize: 13, cursor: historyLoading ? 'not-allowed' : 'pointer',
                opacity: historyLoading ? 0.6 : 1, whiteSpace: 'nowrap', alignSelf: 'flex-end' }}>
              {historyLoading ? '조회 중…' : '🔍 조회'}
            </button>

            {/* 빠른 기간 버튼 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignSelf: 'flex-end' }}>
              <span style={{ fontSize: 11, color: '#555566', fontWeight: 700 }}>빠른 선택</span>
              <div style={{ display: 'flex', gap: 4 }}>
                {[
                  { label: '오늘', days: 0 },
                  { label: '7일', days: 7 },
                  { label: '30일', days: 30 },
                  { label: '90일', days: 90 },
                ].map(({ label, days }) => (
                  <button key={label}
                    onClick={() => {
                      const from = days === 0
                        ? today
                        : new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
                      setFilterDateFrom(from);
                      setFilterDateTo(today);
                    }}
                    style={{ padding: '5px 8px', background: '#2a2a3e', border: '1px solid #3a3a4e',
                      borderRadius: 6, color: '#a09af8', fontSize: 11, fontWeight: 600,
                      cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 건수 표시 */}
          {!historyLoading && (
            <div style={{ fontSize: 12, color: '#555566', marginBottom: 8, textAlign: 'right' }}>
              {historyTotal > 0
                ? `총 ${historyTotal}건 · ${historyPage * PAGE_SIZE + 1}–${Math.min((historyPage + 1) * PAGE_SIZE, historyTotal)}번째 표시`
                : '조회된 이력이 없습니다'}
            </div>
          )}

          {history.length === 0 && !historyLoading && (
            <div style={{ color: 'var(--text-secondary, #8888aa)', fontSize: 14, padding: '16px 0' }}>
              발송 이력이 없습니다
            </div>
          )}

          {history.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#12121f', borderBottom: '2px solid #2a2a3e' }}>
                    {['제목', '발신일시', '발송', '실패', '읽음', '미확인', '읽음현황', '삭제'].map((h, i) => (
                      <th key={i} style={{ padding: '9px 10px', textAlign: i === 0 ? 'left' : 'center',
                        color: '#8888aa', fontWeight: 700, fontSize: 11, letterSpacing: '0.04em',
                        textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, idx) => {
                    const total = h.total || (h.sent + h.failed) || 0;
                    const readPct = total > 0 ? Math.round((h.readCount / total) * 100) : 0;
                    const unread = total > 0 ? total - h.readCount : 0;
                    return (
                      <tr key={h.seq}
                        style={{ borderBottom: '1px solid #1e1e30',
                          background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(108,99,255,0.07)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'; }}>

                        {/* 제목 */}
                        <td style={{ padding: '9px 10px', overflow: 'hidden' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {h.pushSubType === 'individual' ? (
                              <span style={{ fontSize: 10, fontWeight: 700, color: '#f472b6',
                                background: 'rgba(244,114,182,0.12)', borderRadius: 4,
                                padding: '1px 5px', flexShrink: 0 }}>개별</span>
                            ) : (
                              <span style={{ fontSize: 10, fontWeight: 700, color: '#38bdf8',
                                background: 'rgba(56,189,248,0.12)', borderRadius: 4,
                                padding: '1px 5px', flexShrink: 0 }}>전체</span>
                            )}
                            <div style={{ fontWeight: 600, color: '#e8e8f0', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={h.title}>
                              {h.title}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: '#555566', marginTop: 2, paddingLeft: 2 }}>
                            {h.sender && <span>발신: {h.sender}</span>}
                            {h.targetEmail && <span style={{ marginLeft: 8, color: '#f472b6' }}>→ {h.targetEmail}</span>}
                          </div>
                        </td>

                        {/* 발신일시 */}
                        <td style={{ padding: '9px 10px', textAlign: 'center',
                          color: '#8888aa', whiteSpace: 'nowrap', fontSize: 12 }}>
                          {fmtTime(h.createdAt)}
                        </td>

                        {/* 발송수 */}
                        <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                          <span style={{ ...S.badge, ...S.badgeSuccess, padding: '2px 7px' }}>
                            {h.sent}
                          </span>
                        </td>

                        {/* 실패수 */}
                        <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                          {h.failed > 0
                            ? <span style={{ ...S.badge, ...S.badgeFail, padding: '2px 7px' }}>{h.failed}</span>
                            : <span style={{ color: '#444455', fontSize: 12 }}>—</span>}
                        </td>

                        {/* 읽음수 */}
                        <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                          <span style={{ ...S.badge, ...S.badgeRead, padding: '2px 7px', cursor: 'default' }}>
                            {h.readCount}
                          </span>
                        </td>

                        {/* 미확인수 */}
                        <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                          {unread > 0
                            ? <span style={{ ...S.badge, ...S.badgeUnread, padding: '2px 7px' }}>{unread}</span>
                            : total > 0
                              ? <span style={{ color: '#4ade80', fontSize: 12, fontWeight: 600 }}>✓ 전원</span>
                              : <span style={{ color: '#444455', fontSize: 12 }}>—</span>}
                        </td>

                        {/* 읽음현황 버튼 */}
                        <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                          <div>
                            <button
                              onClick={() => openReaders(h.seq)}
                              style={{ fontSize: 11, fontWeight: 700, color: '#a09af8',
                                background: 'rgba(108,99,255,0.18)', border: '1px solid rgba(108,99,255,0.35)',
                                borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                                whiteSpace: 'nowrap' }}>
                              👁 상세
                            </button>
                            {total > 0 && (
                              <div style={{ marginTop: 4, height: 3, borderRadius: 3,
                                background: '#2a2a3e', overflow: 'hidden' }}>
                                <div style={{ height: '100%', borderRadius: 3,
                                  background: readPct === 100
                                    ? '#4ade80'
                                    : 'linear-gradient(90deg,#6c63ff,#4ade80)',
                                  width: `${readPct}%`, transition: 'width .4s' }} />
                              </div>
                            )}
                          </div>
                        </td>

                        {/* 삭제 버튼 */}
                        <td style={{ padding: '9px 8px', textAlign: 'center' }}>
                          <button
                            onClick={() => setDeleteTarget({ seq: h.seq, title: h.title })}
                            title="발송 이력 삭제 (개인 수신함에서도 제거됨)"
                            style={{ fontSize: 13, background: 'none',
                              border: '1px solid rgba(239,68,68,0.35)',
                              borderRadius: 6, color: '#f87171',
                              cursor: 'pointer', padding: '3px 8px',
                              transition: 'background 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                            🗑
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* 페이지네이션 */}
          {historyTotal > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
              gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                disabled={historyPage === 0}
                onClick={() => handlePageChange(0)}
                style={{ padding: '5px 10px', background: '#12121f', border: '1px solid #2a2a3e',
                  borderRadius: 6, color: historyPage === 0 ? '#444455' : '#a09af8',
                  cursor: historyPage === 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>
                «
              </button>
              <button
                disabled={historyPage === 0}
                onClick={() => handlePageChange(historyPage - 1)}
                style={{ padding: '5px 12px', background: '#12121f', border: '1px solid #2a2a3e',
                  borderRadius: 6, color: historyPage === 0 ? '#444455' : '#a09af8',
                  cursor: historyPage === 0 ? 'not-allowed' : 'pointer', fontSize: 12 }}>
                ‹ 이전
              </button>

              {Array.from({ length: Math.ceil(historyTotal / PAGE_SIZE) }, (_, i) => i)
                .filter(i => Math.abs(i - historyPage) <= 2)
                .map(i => (
                  <button key={i} onClick={() => handlePageChange(i)}
                    style={{ padding: '5px 10px', fontSize: 12, fontWeight: i === historyPage ? 700 : 400,
                      border: '1px solid #2a2a3e', borderRadius: 6, cursor: 'pointer',
                      background: i === historyPage ? 'linear-gradient(135deg,#6c63ff,#5a52d5)' : '#12121f',
                      color: i === historyPage ? '#fff' : '#8888aa' }}>
                    {i + 1}
                  </button>
                ))}

              <button
                disabled={(historyPage + 1) * PAGE_SIZE >= historyTotal}
                onClick={() => handlePageChange(historyPage + 1)}
                style={{ padding: '5px 12px', background: '#12121f', border: '1px solid #2a2a3e',
                  borderRadius: 6,
                  color: (historyPage + 1) * PAGE_SIZE >= historyTotal ? '#444455' : '#a09af8',
                  cursor: (historyPage + 1) * PAGE_SIZE >= historyTotal ? 'not-allowed' : 'pointer',
                  fontSize: 12 }}>
                다음 ›
              </button>
              <button
                disabled={(historyPage + 1) * PAGE_SIZE >= historyTotal}
                onClick={() => handlePageChange(Math.ceil(historyTotal / PAGE_SIZE) - 1)}
                style={{ padding: '5px 10px', background: '#12121f', border: '1px solid #2a2a3e',
                  borderRadius: 6,
                  color: (historyPage + 1) * PAGE_SIZE >= historyTotal ? '#444455' : '#a09af8',
                  cursor: (historyPage + 1) * PAGE_SIZE >= historyTotal ? 'not-allowed' : 'pointer',
                  fontSize: 12 }}>
                »
              </button>
              <span style={{ fontSize: 12, color: '#555566', marginLeft: 4 }}>
                {historyPage + 1} / {Math.ceil(historyTotal / PAGE_SIZE)} 페이지
              </span>
            </div>
          )}
        </div>
      </div>

        </div>{/* /rightCol */}
        </div>{/* /twoCol */}

      {/* 삭제 확인 모달 */}
      {deleteTarget && (
        <div style={S.overlay} onClick={() => !deleting && setDeleteTarget(null)}>
          <div style={{ ...S.modal, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 12 }}>🗑️</div>
            <div style={{ ...S.modalTitle, textAlign: 'center', marginBottom: 8 }}>
              발송 이력 삭제
            </div>
            <div style={{ fontSize: 13, color: '#8888aa', textAlign: 'center',
              lineHeight: 1.6, marginBottom: 20 }}>
              <strong style={{ color: '#e8e8f0' }}>「{deleteTarget.title}」</strong> 이력을 삭제합니다.<br />
              <span style={{ color: '#f87171', fontSize: 12 }}>
                ⚠️ 모든 수신자의 개인 알림함에서도 함께 삭제됩니다.
              </span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                style={{ flex: 1, padding: '11px 0', background: '#2a2a3e', border: 'none',
                  borderRadius: 8, color: '#aaa', cursor: 'pointer', fontSize: 14 }}>
                취소
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ flex: 1, padding: '11px 0',
                  background: deleting ? '#555' : 'linear-gradient(135deg,#ef4444,#dc2626)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  fontWeight: 700, cursor: deleting ? 'not-allowed' : 'pointer', fontSize: 14 }}>
                {deleting ? '삭제 중…' : '삭제 확인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 읽음 현황 모달 */}
      {readersModal && (
        <div style={S.overlay} onClick={() => setReadersModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            {readersLoading || !readersModal.data ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#8888aa' }}>불러오는 중…</div>
            ) : (
              <>
                <div style={S.modalTitle}>👁 읽음 현황</div>
                <div style={{ ...S.modalBody, marginBottom: 4 }}>{readersModal.data.title}</div>
                <div style={{ color: '#555566', fontSize: 12, marginBottom: 16 }}>{fmt(readersModal.data.createdAt)}</div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  {[
                    { val: readersModal.data.readCount, label: '읽음', color: '#a09af8', bg: 'rgba(108,99,255,0.15)' },
                    { val: readersModal.data.unreadCount, label: '미확인', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
                    {
                      val: `${readersModal.data.readCount + readersModal.data.unreadCount > 0
                        ? Math.round(readersModal.data.readCount / (readersModal.data.readCount + readersModal.data.unreadCount) * 100)
                        : 0}%`,
                      label: '읽음율', color: '#4ade80', bg: 'rgba(74,222,128,0.1)',
                    },
                  ].map(({ val, label, color, bg }) => (
                    <div key={label} style={{ flex: 1, background: bg, borderRadius: 8, padding: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color }}>{val}</div>
                      <div style={{ fontSize: 11, color: '#8888aa', marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>

                <div style={S.tabRow}>
                  {[['read', `👁 읽음 (${readersModal.data.readCount}명)`], ['unread', `⏳ 미확인 (${readersModal.data.unreadCount}명)`]].map(([k, label]) => (
                    <div key={k} style={{ ...S.tab, ...(readersTab === k ? S.tabActive : {}) }} onClick={() => setReadersTab(k)}>
                      {label}
                    </div>
                  ))}
                </div>

                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {readersTab === 'read' && (
                    readersModal.data.readers.length === 0
                      ? <div style={{ color: '#8888aa', fontSize: 13, padding: '16px 0' }}>아직 읽은 사람이 없습니다</div>
                      : readersModal.data.readers.map((p, i) => (
                          <div key={i} style={S.personRow}>
                            <div style={{ ...S.avatar, background: avatarColor(p.name) }}>
                              {(p.name || '?').charAt(0)}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                              <div style={{ color: '#8888aa', fontSize: 11 }}>{p.employee_id}</div>
                            </div>
                            <div style={{ color: '#6c63ff', fontSize: 12 }}>{fmtTime(p.readAt)}</div>
                          </div>
                        ))
                  )}
                  {readersTab === 'unread' && (
                    readersModal.data.unread.length === 0
                      ? <div style={{ color: '#4ade80', fontSize: 13, padding: '16px 0' }}>🎉 모든 직원이 읽었습니다!</div>
                      : readersModal.data.unread.map((p, i) => (
                          <div key={i} style={S.personRow}>
                            <div style={{ ...S.avatar, background: '#2a2a3e' }}>
                              {(p.name || '?').charAt(0)}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                              <div style={{ color: '#8888aa', fontSize: 11 }}>{p.employee_id}</div>
                            </div>
                            <div style={{ color: '#fbbf24', fontSize: 12 }}>미확인</div>
                          </div>
                        ))
                  )}
                </div>

                <button style={S.closeBtn} onClick={() => setReadersModal(null)}>닫기</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
